import buffer from "@turf/buffer";
import { useQuery, QueryHookOptions } from "@apollo/client";
import { SelectionGeometry } from "src/js/stores/map/store";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import { SURFACE_FLOOD_CATEGORIES, SURFACE_FLOOD_CLIMATE_CATEGORIES } from "../constants";
import SURFACE_FLOOD_SUMMARY_QUERY from "./SurfaceFloodingSummaryQuery.gql";
import { sortByCategoryList } from "./sortByCategoryList";
import { useMemo } from "react";
import { DesignationArea } from "react-migration/domains/constraints/designation/hooks/useDesignationAreaByGeometry";

interface SurfaceFloodingSummaryQueryResult {
  surfaceFloodCoverage: DesignationArea[];
  surfaceFloodClimateCoverage: DesignationArea[];
}

interface SurfaceFloodingSummaryQueryVariables {
  geometry: SelectionGeometry;
  surfaceFloodCategories: string[];
  surfaceFloodClimateCategories: string[];
}

export function useSurfaceFloodingSummaryQuery(
  geometry: SelectionGeometry | null | undefined,
  opts?: Partial<QueryHookOptions<SurfaceFloodingSummaryQueryResult, SurfaceFloodingSummaryQueryVariables>>
) {
  const bufferedGeometry = geometry && bufferGeometry(geometry);

  const { data, ...rest } = useQuery<SurfaceFloodingSummaryQueryResult, SurfaceFloodingSummaryQueryVariables>(
    SURFACE_FLOOD_SUMMARY_QUERY,
    {
      variables: {
        geometry: bufferedGeometry!,
        surfaceFloodCategories: SURFACE_FLOOD_CATEGORIES,
        surfaceFloodClimateCategories: SURFACE_FLOOD_CLIMATE_CATEGORIES,
      },
      client: routedClient,
      context: {
        endpoint: LandTechEndpoints.Constraints,
      },

      ...opts,
      skip: !bufferedGeometry || opts?.skip,
    }
  );

  const { surfaceFloodCoverage, surfaceFloodClimateCoverage } = useMemo(
    () => ({
      surfaceFloodCoverage: sortByCategoryList(data?.surfaceFloodCoverage, SURFACE_FLOOD_CATEGORIES),
      surfaceFloodClimateCoverage: sortByCategoryList(
        data?.surfaceFloodClimateCoverage,
        SURFACE_FLOOD_CLIMATE_CATEGORIES
      ),
    }),
    [data]
  );

  return {
    surfaceFloodCoverage,
    surfaceFloodClimateCoverage,
    ...rest,
  };
}

/**
 * Want to ensure that point/line selections result in a flooding overlap
 */
function bufferGeometry(geometry: SelectionGeometry): SelectionGeometry {
  if (["Polygon", "MultiPolygon"].includes(geometry.type)) return geometry;

  return buffer(geometry, 1, { units: "meters" }).geometry;
}
